import { useState } from 'react';
import { useToast } from '../components/Toast';
import { deleteAllContentInDraft } from '../helpers/deleteAllContentInDraft';
import { deleteAllImagesInDraft } from '../helpers/deleteAllImagesInDraft';
import { TextBlock } from '../interfaces/textEditor.interface';

export const useContentDeletion = () => {
  const { showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteAllDraftContent = async (
    textBlocks: TextBlock[],
    userId: string,
    draftId: string,
    contentRef?: string,
  ) => {
    setIsDeleting(true);

    try {
      const results = await deleteAllContentInDraft(
        textBlocks,
        userId,
        draftId,
        contentRef,
      );

      // Log results
      const imagesFailed = results.imageResults.failed.length;
      const imagesSuccess = results.imageResults.success.length;
      const contentSuccess = results.contentResults.success;
      const contentError = results.contentResults.error;

      if (imagesFailed > 0 || contentError) {
        // error, not warn: failed image deletes leave objects in a PUBLICLY READABLE bucket after
        // the user discarded the draft they belonged to.
        console.error('Some content failed to delete:', {
          imagesFailed,
          contentError,
        });
      }

      // ...and TOLD to the user. THIS is the live discard path — both composers call
      // deleteAllDraftContent. An earlier revision put this toast on `deleteDraftImages`, which has
      // no production callers at all, so the "fix" ran on nothing and its test pinned a function
      // the product never invokes. Verifying the function I wrote instead of the flow the user
      // takes is the same mistake as the bug being fixed here.
      if (imagesFailed > 0) {
        const total = imagesFailed + imagesSuccess;
        showToast({
          title: 'Some images could not be deleted',
          body:
            imagesFailed === total
              ? 'The draft was discarded, but its images may still be accessible.'
              : `The draft was discarded, but ${imagesFailed} of ${total} images may still be accessible.`,
          variant: 'warning',
        });
      }

      return results;
    } catch (error) {
      console.error('Error during bulk content deletion:', error);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteDraftImages = async (textBlocks: TextBlock[]) => {
    setIsDeleting(true);

    try {
      const results = await deleteAllImagesInDraft(textBlocks);

      if (results.failed.length > 0) {
        const total = results.success.length + results.failed.length;
        // error, not warn: these objects are still in a PUBLICLY READABLE bucket after the user
        // discarded the draft they belonged to.
        console.error(
          `${results.failed.length} of ${total} images were NOT deleted and remain accessible`,
          results.failed,
        );
        // NOTE: this function has NO production callers — both discard flows go through
        // deleteAllDraftContent above, which is where the live toast lives. Kept in sync so the two
        // do not drift if something starts calling it, but it is not what protects users today.
        showToast({
          title: 'Some images could not be deleted',
          body:
            results.failed.length === total
              ? 'The draft was discarded, but its images may still be accessible.'
              : `The draft was discarded, but ${results.failed.length} of ${total} images may still be accessible.`,
          variant: 'warning',
        });
      }

      return results;
    } catch (error) {
      console.error('Error during bulk image deletion:', error);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return { deleteAllDraftContent, deleteDraftImages, isDeleting };
};
